import React, { memo, useEffect } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { useForm } from 'react-hook-form'

import ErrorHandler from '@core/api/ErrorHandler'
import { TextField, TextFieldProps } from '@core/components/textfield/textfield'
import { queryKeys, useCreateFolder } from '@core/folders/folders-api'
import { addSnack } from '@core/snack/snack-state'
import { useQueryClient } from '@tanstack/react-query'

import { useProjectsState, type Project } from './projects-state'

type Values = {
  name: string
}

const defaultValues: Values = {
  name: '',
}

const useCreateFolderDeliver = () => {
  const createFolder = useCreateFolder()

  return async (name: string) => {
    return createFolder
      .mutateAsync({ name })
      .then((data) => {
        return {
          success: true,
          project: { ...data, ownerId: data.owner?.id, access: [] } as Project,
          reason: null,
        }
      })
      .catch((error) => {
        const { errorMessage } = ErrorHandler(error)

        addSnack({
          message: errorMessage || 'Something went wrong',
          severity: 'error',
        })

        return {
          success: false,
          reason: error.message,
          project: null,
        }
      })
  }
}

type ProjectEditContentProps = {
  form: ReturnType<typeof useForm<Values>>
  onSubmit: (values: Values) => Promise<void>
}

const ProjectEditContent = (props: ProjectEditContentProps) => {
  const onCloseProjectEdit = useProjectsState((state) => state.onCloseProjectEdit)

  const { handleSubmit, formState, register } = props.form

  const getProps = (field: keyof Values): TextFieldProps => {
    const error = formState.errors[field]
    let message = error?.message

    if (error?.type === 'maxLength') {
      message = 'Max length of 128'
    } else if (error?.type === 'minLength') {
      message = 'Min length of 3'
    }

    return {
      error: Boolean(error),
      helperText: message,
    }
  }

  const actionName = 'Create'

  return (
    <>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Typography variant="h3" sx={{ flexGrow: 1 }}>
            {actionName} Folder
          </Typography>
          <IconButton
            sx={{ mr: 0, flexShrink: 0 }}
            edge="end"
            aria-label="close"
            onClick={onCloseProjectEdit}
            disabled={formState.isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(props.onSubmit)}>
        <DialogContent sx={{ overflowY: 'initial', pt: 2 }}>
          <TextField
            autoFocus
            required
            {...register('name', {
              maxLength: 128,
              minLength: 3,
              required: 'Required',
            })}
            {...getProps('name')}
            disabled={formState.isSubmitting}
            label="Folder Title"
            aria-label="Folder Title"
            fullWidth
            autoComplete="off"
          />
        </DialogContent>

        <DialogActions sx={{ mt: 2 }}>
          <Button onClick={onCloseProjectEdit} color="secondary" disabled={formState.isSubmitting}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="secondary"
            type="submit"
            disabled={formState.isSubmitting || !formState.isDirty}
            loading={formState.isSubmitting}
          >
            {actionName}
          </LoadingButton>
        </DialogActions>
      </Box>
    </>
  )
}

export const ProjectEdit = () => {
  const { open, onUpdate } = useProjectsState((state) => state.edit)
  const createFolderMutation = useCreateFolderDeliver()
  const onCloseProjectEdit = useProjectsState((state) => state.onCloseProjectEdit)
  const queryClient = useQueryClient()

  const form = useForm({ defaultValues })
  const { formState } = form

  useEffect(() => {
    if (!open) return

    form.reset(defaultValues)
  }, [form, open])

  const onSubmit = async (values: Values) => {
    const result = await createFolderMutation(values.name)

    if (result.success) {
      if (onUpdate && result?.project) {
        onUpdate(result.project)
      }

      await queryClient.refetchQueries({ queryKey: queryKeys.all })

      onCloseProjectEdit()
    } else {
      form.setError('name', { message: result.reason })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={formState.isSubmitting ? undefined : onCloseProjectEdit}
      maxWidth="xs"
      fullWidth
      sx={{ '& .MuiPaper-root': { maxWidth: 480, px: 1.5, py: 2 } }}
    >
      <ProjectEditContent form={form} onSubmit={onSubmit} />
    </Dialog>
  )
}

export default memo(ProjectEdit)
